// Billing & plans and "My businesses" (PlatformAPI BillingEndpoints / CustomerHubEndpoints, PlatformDB 054).
import { read, write } from '../hub/hubApi';

export interface Plan {
  code: string;
  productCode: string;
  name: string;
  description: string | null;
  price: number;
  currencyCode: string;
  billingPeriod: 'Month' | 'Year';
  tier: number;
  isActive: boolean;
  features: string[];
}
export interface ProductPlans {
  productCode: string;
  displayName: string;
  icon: string | null;
  subscription: { source: string; planCode: string | null; expiresOn: string | null; isActive: boolean } | null;
  pendingRequest: { id: string; planCode: string; requestedAt: string } | null;
  plans: Plan[];
}
export type RequestStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled';
export interface PlanRequest {
  id: string;
  organizationId: string;
  organizationName: string;
  productCode: string;
  productName: string;
  planCode: string;
  planName: string;
  price: number;
  currencyCode: string;
  billingPeriod: 'Month' | 'Year';
  status: RequestStatus;
  note: string | null;
  reviewNote: string | null;
  requestedBy: string;
  requestedByEmail: string;
  requestedAt: string;
  reviewedAt: string | null;
  startsOn: string | null;
  endsOn: string | null;
}
export interface Subscription {
  organizationId: string;
  organizationName: string;
  productCode: string;
  productName: string;
  source: 'Legacy' | 'Billing' | 'Manual';
  planCode: string | null;
  enabledAt: string;
  expiresOn: string | null;
  disabledAt: string | null;
  isActive: boolean;
}
export interface BusinessProduct {
  code: string;
  displayName: string;
  icon: string | null;
  source: string;
  planCode: string | null;
  expiresOn: string | null;
  isActive: boolean;
  isAssigned: boolean;
}
export interface Business {
  id: string;
  code: string;
  name: string;
  city: string | null;
  slug: string | null;
  isVerified: boolean;
  isActive: boolean;
  roles: string[];
  products: BusinessProduct[];
  pendingRequests: { productCode: string; planCode: string }[];
}

export const billingApi = {
  ownSubscriptions: (signal?: AbortSignal) => read<Subscription[]>('/billing/subscriptions', undefined, signal),
  plans: (signal?: AbortSignal) => read<ProductPlans[]>('/billing/plans', undefined, signal),
  myRequests: (signal?: AbortSignal) => read<PlanRequest[]>('/billing/requests', undefined, signal),
  request: (planCode: string, note: string | null) => write<{ id: string }>('/billing/requests', { planCode, note }, 'post'),
  cancel: (id: string) => write(`/billing/requests/${id}/cancel`, {}, 'post'),
  allRequests: (status: string, signal?: AbortSignal) =>
    read<PlanRequest[]>('/billing/admin/requests', status ? { status } : undefined, signal),
  approve: (id: string, reviewNote: string | null) => write(`/billing/admin/requests/${id}/approve`, { reviewNote }, 'post'),
  reject: (id: string, reviewNote: string | null) => write(`/billing/admin/requests/${id}/reject`, { reviewNote }, 'post'),
  updatePlan: (plan: Plan) =>
    write<Plan>(`/billing/admin/plans/${encodeURIComponent(plan.code)}`, {
      name: plan.name,
      description: plan.description,
      price: plan.price,
      billingPeriod: plan.billingPeriod,
      features: plan.features,
      isActive: plan.isActive,
    }),
  subscriptions: (signal?: AbortSignal) => read<Subscription[]>('/billing/admin/subscriptions', undefined, signal),
  disable: (organizationId: string, productCode: string) =>
    write(`/billing/admin/organizations/${organizationId}/products/${productCode}/disable`, {}, 'post'),
  businesses: (signal?: AbortSignal) => read<Business[]>('/me/businesses', undefined, signal),
};

export function formatPrice(amount: number, currency: string, language?: string) {
  try {
    return new Intl.NumberFormat(language || undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

export function formatDate(value: string | null, language?: string) {
  if (!value) return '';
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(language || undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
